import { useNavigate, useParams } from "react-router-dom"
import { ArrowLeft, Edit, Trash2, RefreshCw, Target, TrendingUp, AlertTriangle, Calendar, Clock, User } from "lucide-react"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts"
import { useGoal, useDeleteGoal, useRecalculateGoal } from "@/hooks/use-goals"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { toast } from "sonner"
import type { GoalMetricType, GoalStatus } from "@/lib/types"

const METRIC_LABELS: Record<GoalMetricType, string> = {
  deals_won: "Wygrane transakcje",
  deals_value: "Wartość transakcji",
  activities_count: "Liczba aktywności",
  tasks_completed: "Ukończone zadania",
  revenue: "Przychód",
  calls_made: "Wykonane telefony",
  meetings_held: "Spotkania",
  new_contacts: "Nowe kontakty",
  new_companies: "Nowi kontrahenci",
  custom: "Własna metryka",
}

const PERIOD_LABELS: Record<string, string> = {
  weekly: "Tygodniowy",
  monthly: "Miesięczny",
  quarterly: "Kwartalny",
  yearly: "Roczny",
  custom: "Własny",
}

const STATUS_CONFIG: Record<GoalStatus, { label: string; color: string }> = {
  active: { label: "Aktywny", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" },
  completed: { label: "Osiągnięty", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" },
  failed: { label: "Nieosiągnięty", color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" },
  cancelled: { label: "Anulowany", color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300" },
}

function formatValue(value: number, metricType: string): string {
  if (metricType === "deals_value" || metricType === "revenue") {
    return new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN", maximumFractionDigits: 0 }).format(value)
  }
  return value.toLocaleString("pl-PL")
}

function formatDate(dateStr: string): string {
  // Date-only strings (YYYY-MM-DD) are parsed as UTC midnight — use UTC methods to avoid timezone shift
  const [y, m, d] = dateStr.split("-")
  return `${d}.${m}.${y}`
}

export function GoalDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { data: goalData, isLoading } = useGoal(id || "")
  const deleteGoal = useDeleteGoal()
  const recalculate = useRecalculateGoal()

  const goal = goalData?.data

  const handleDelete = () => {
    if (!goal) return
    if (!window.confirm("Czy na pewno chcesz usunąć ten cel?")) return
    deleteGoal.mutate(goal.id, {
      onSuccess: () => {
        toast.success("Cel usunięty")
        navigate("/goals")
      },
      onError: () => toast.error("Błąd usuwania celu"),
    })
  }

  const handleRecalculate = () => {
    if (!goal) return
    recalculate.mutate(goal.id, {
      onSuccess: () => toast.success("Postęp przeliczony"),
      onError: () => toast.error("Błąd przeliczania"),
    })
  }

  if (isLoading) {
    return (
      <div className="p-6 max-w-[960px] mx-auto space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[200px]" />
        <Skeleton className="h-[300px]" />
      </div>
    )
  }

  if (!goal) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Nie znaleziono celu</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/goals")}>
          Wróć do listy
        </Button>
      </div>
    )
  }

  const statusCfg = STATUS_CONFIG[goal.status]
  const progressColor = goal.status === "completed" ? "#22c55e" : goal.status === "failed" ? "#ef4444" : goal.on_track ? "#3b82f6" : "#f59e0b"

  // Prepare chart data
  const chartData = (goal.history || []).map((h) => ({
    date: new Date(h.date).toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit" }),
    value: h.value,
  }))

  return (
    <div className="p-6 max-w-[960px] mx-auto">
      <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate("/goals")}>
        <ArrowLeft className="h-4 w-4 mr-1" />
        Cele
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-foreground">{goal.name}</h1>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>
              {statusCfg.label}
            </span>
          </div>
          {goal.description && (
            <p className="text-sm mt-1 text-muted-foreground">{goal.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRecalculate} disabled={recalculate.isPending}>
            <RefreshCw className={`h-4 w-4 mr-1 ${recalculate.isPending ? "animate-spin" : ""}`} />
            Przelicz
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/goals/${goal.id}/edit`)}>
            <Edit className="h-4 w-4 mr-1" />
            Edytuj
          </Button>
          <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-600 hover:text-red-700">
            <Trash2 className="h-4 w-4 mr-1" />
            Usuń
          </Button>
        </div>
      </div>

      {/* Progress section */}
      <div className="rounded-lg border p-6 mb-6 bg-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Postęp</p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-3xl font-bold text-foreground">
                {formatValue(goal.current_value, goal.metric_type)}
              </span>
              <span className="text-lg text-muted-foreground">
                / {formatValue(goal.target_value, goal.metric_type)}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div
              className="text-4xl font-bold"
              style={{ color: progressColor }}
            >
              {goal.progress_percent.toFixed(0)}%
            </div>
            <div className="flex items-center gap-1 mt-1">
              {goal.status === "active" && goal.on_track && (
                <>
                  <TrendingUp className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600">Na dobrej drodze</span>
                </>
              )}
              {goal.status === "active" && !goal.on_track && (
                <>
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <span className="text-sm text-amber-600">Zagrożony</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Large progress bar */}
        <div className="h-4 rounded-full bg-muted">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${Math.min(goal.progress_percent, 100)}%`,
              backgroundColor: progressColor,
            }}
          />
        </div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="rounded-lg border p-4 bg-card">
          <div className="flex items-center gap-2 mb-2">
            <Target className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Metryka</span>
          </div>
          <p className="font-semibold text-sm text-foreground">{METRIC_LABELS[goal.metric_type]}</p>
        </div>
        <div className="rounded-lg border p-4 bg-card">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Okres</span>
          </div>
          <p className="font-semibold text-sm text-foreground">
            {formatDate(goal.start_date)} - {formatDate(goal.end_date)}
          </p>
        </div>
        <div className="rounded-lg border p-4 bg-card">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Czas</span>
          </div>
          <p className="font-semibold text-sm text-foreground">
            {goal.status === "active"
              ? `Pozostało ${goal.days_remaining} z ${goal.days_total} dni`
              : `${goal.days_elapsed} z ${goal.days_total} dni`
            }
          </p>
        </div>
        <div className="rounded-lg border p-4 bg-card">
          <div className="flex items-center gap-2 mb-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Przypisany</span>
          </div>
          <p className="font-semibold text-sm text-foreground">
            {goal.user ? `${goal.user.first_name} ${goal.user.last_name}` : "Zespół"}
          </p>
        </div>
      </div>

      {/* Progress chart */}
      {chartData.length > 1 && (
        <div className="rounded-lg border p-6 bg-card">
          <h3 className="font-semibold mb-4 text-foreground">Historia postępu</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: "var(--muted-foreground)" }} />
                <YAxis tick={{ fontSize: 12, fill: "var(--muted-foreground)" }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "var(--card)",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                />
                <ReferenceLine
                  y={goal.target_value}
                  stroke="#ef4444"
                  strokeDasharray="5 5"
                  label={{ value: "Cel", position: "right", fill: "#ef4444", fontSize: 12 }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={progressColor}
                  strokeWidth={2}
                  dot={{ fill: progressColor, r: 4 }}
                  name="Wartość"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  )
}
